// Push Notification Utilities

import config from '../config';

// Use config for API base URL
const API_BASE = config.apiUrl;

// Convert VAPID key from base64url to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Check if push notifications are supported
export function isPushSupported() {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

// Get notification permission
export async function getNotificationPermission() {
  if (!isPushSupported()) {
    console.warn('Push messaging is not supported');
    return 'not-supported';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    return 'denied';
  }

  // Request permission
  const permission = await Notification.requestPermission();
  return permission;
}

// Register service worker
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service Worker not supported');
  }

  try {
    console.log('Checking for service worker...');
    
    // First, check if service worker is already registered and ready
    const existingRegistrations = await navigator.serviceWorker.getRegistrations();
    
    if (existingRegistrations.length > 0) {
      console.log('✅ Service worker already registered, reusing it...');
      // Use the existing registration
      const readyRegistration = await navigator.serviceWorker.ready;
      console.log('✅ Service Worker is ready');
      return readyRegistration;
    }

    // Only register if none exists
    console.log('Registering new service worker...');
    const _registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
      updateViaCache: 'none'
    });

    console.log('Service Worker registered successfully');

    // Wait for service worker to be ready
    const readyRegistration = await navigator.serviceWorker.ready;
    console.log('✅ Service Worker is ready');
    
    return readyRegistration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    throw error;
  }
}

// Get VAPID public key from server
export async function getVapidPublicKey() {
  try {
    console.log('Fetching VAPID public key from:', `${API_BASE}/api/push/public-key`);
    const response = await fetch(`${API_BASE}/api/push/public-key`);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('VAPID key request failed:', response.status, errorText);
      throw new Error(`VAPID key not available (${response.status}). Server may not be configured properly.`);
    }
    
    const data = await response.json();
    
    if (!data.publicKey) {
      throw new Error('VAPID public key not found in response');
    }
    
    console.log('VAPID public key received successfully');
    return data.publicKey;
  } catch (error) {
    console.error('Error getting VAPID public key:', error);
    if (error.message.includes('Failed to fetch')) {
      throw new Error('Cannot connect to server. Please check your internet connection.');
    }
    throw error;
  }
}

// Subscribe to push notifications
export async function subscribeToPush(userId) {
  try {
    //console.log('Starting push subscription for user:', userId);
    
    if (!isPushSupported()) {
      throw new Error('Push notifications not supported in this browser');
    }

    // Check if running on localhost or HTTPS
    if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
      console.warn('Push notifications require HTTPS in production');
    }

    // Get notification permission
    //console.log('Requesting notification permission...');
    const permission = await getNotificationPermission();
    //console.log('Notification permission:', permission);
    
    if (permission !== 'granted') {
      throw new Error(`Notification permission ${permission}`);
    }

    // Register service worker
    console.log('Registering service worker...');
    const registration = await registerServiceWorker();

    // Check if push manager is available
    if (!registration.pushManager) {
      throw new Error('Push messaging is not supported');
    }
    
    // Chrome/mobile requires extra wait for service worker to be fully active
    if (registration.installing || registration.waiting) {
      console.log('Service worker is installing/waiting, waiting for activation...');
      await new Promise((resolve) => {
        const worker = registration.installing || registration.waiting;
        worker.addEventListener('statechange', () => {
          if (worker.state === 'activated') {
            console.log('✅ Service worker activated');
            resolve();
          }
        });
        // Timeout after 5 seconds
        setTimeout(resolve, 5000);
      });
    }

    // Get VAPID public key
    //console.log('Getting VAPID public key...');
    const vapidPublicKey = await getVapidPublicKey();
    //console.log('VAPID public key received:', vapidPublicKey.substring(0, 20) + '...');
    
    const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);

    // Check for existing subscription
    const existingSubscription = await registration.pushManager.getSubscription();
    if (existingSubscription) {
      console.log('Found existing subscription, checking if it\'s valid...');
      // Check if existing subscription is for the same VAPID key
      try {
        // If we can convert the existing subscription, it's likely valid
        const existingKey = existingSubscription.options.applicationServerKey;
        if (existingKey) {
          console.log('✅ Existing subscription is valid, reusing it...');
          // Save to server and return (don't create new subscription)
          const response = await fetch(`${API_BASE}/api/push/subscribe`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              userId: userId,
              subscription: existingSubscription.toJSON()
            })
          });
          
          if (response.ok) {
            console.log('✅ Existing subscription saved to server');
            return existingSubscription;
          }
        }
      } catch (e) {
        console.log('Existing subscription invalid, will create new one');
      }
      
      // Unsubscribe if we need to create a new one
      console.log('Unsubscribing from old subscription...');
      await existingSubscription.unsubscribe();
    }

    // Subscribe to push
    console.log('Creating new push subscription...');
    let subscription;
    try {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey
      });
      console.log('✅ Push subscription successful:', subscription.endpoint);
    } catch (subscribeError) {
      console.error('Push subscription failed:', subscribeError);
      
      // Handle specific error types
      if (subscribeError.name === 'AbortError' || subscribeError.message.includes('Registration failed')) {
        throw new Error('Browser push service error. This can happen due to:\n' +
          '1. Invalid VAPID keys on server\n' +
          '2. Network connectivity issues\n' +
          '3. Too many subscription attempts\n' +
          'Solution: Wait 1 minute and try again, or contact support if issue persists.');
      }
      throw subscribeError;
    }

    // Send subscription to server
    console.log('Saving subscription to server...');
    const response = await fetch(`${API_BASE}/api/push/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        userId: userId,
        subscription: subscription.toJSON()
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to save subscription:', response.status, errorText);
      try {
        const errorData = JSON.parse(errorText);
        throw new Error(errorData.error || 'Failed to save subscription to server');
      } catch (e) {
        throw new Error(`Failed to save subscription (${response.status})`);
      }
    }

    console.log('✅ Push subscription saved to server successfully');
    return subscription;

  } catch (error) {
    console.error('Error subscribing to push notifications:', error);
    // console.error('Error details:', {
    //   name: error.name,
    //   message: error.message,
    //   stack: error.stack
    // });
    throw error;
  }
}

// Unsubscribe from push notifications
export async function unsubscribeFromPush(userId) {
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      //console.log('No service worker registration found');
      return;
    }

    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      //console.log('No push subscription found');
      return;
    }

    // Unsubscribe from push
    await subscription.unsubscribe();

    // Remove subscription from server
    await fetch(`${API_BASE}/api/push/unsubscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        userId: userId,
        endpoint: subscription.endpoint
      })
    });

    //console.log('Unsubscribed from push notifications');
  } catch (error) {
    console.error('Error unsubscribing from push notifications:', error);
    throw error;
  }
}

// Check if user is subscribed
export async function isSubscribed() {
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      return false;
    }

    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch (error) {
    console.error('Error checking subscription status:', error);
    return false;
  }
}

// Handle notification clicks from service worker
export function handleNotificationMessages(callback) {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'NOTIFICATION_CLICK') {
        callback(event.data);
      }
    });
  }
}

// Show local notification (for testing)
export function showLocalNotification(title, options = {}) {
  if (Notification.permission === 'granted') {
    new Notification(title, {
      body: options.body || 'Test notification',
      icon: options.icon || '/default-avatar.png',
      badge: '/notification-badge.png',
      ...options
    });
  }
}
